import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
  getListTeachersQueryKey,
  useCreateTeacher,
  useListTeachers,
} from '../../api/generated/enrollment/enrollment';
import type { CivilNameInput, Locale, Me } from '../../api/generated/model';
import { isProblemError } from '../../api/client';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { AdminOnly, usePager } from './AdminOnly';
// Erro e vazio nunca juntos: a regra mora num lugar so, e nao em cada tela.
import { ListOutcome } from './ListOutcome';
import { CivilNameField, emptyCivilName, isCivilNameComplete } from './CivilNameField';
import { SelectField } from './SelectField';
import { isValidTimezone } from './timezone';
import styles from './enrollment.module.css';

const LOCALES: readonly Locale[] = ['pt-BR', 'en-US', 'es'];

/** Lista de professores e cadastro de professor novo. Exclusiva de Super Admin. */
export function TeachersPage() {
  return <AdminOnly>{(me) => <TeachersView me={me} />}</AdminOnly>;
}

function TeachersView({ me }: { readonly me: Me }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pager = usePager();
  const list = useListTeachers(pager.cursor === undefined ? {} : { cursor: pager.cursor });

  const items = list.data?.items ?? [];
  const nextCursor = list.data?.nextCursor ?? null;

  return (
    <div className={styles.page}>
      <h1>{t('enrollment.teachers.title')}</h1>

      <ListOutcome
        isPending={list.isPending}
        isError={list.isError}
        count={items.length}
        empty={t('enrollment.teachers.empty')}
      />

      <table className={styles.table}>
        <caption className="visually-hidden">{t('enrollment.teachers.title')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('enrollment.displayName')}</th>
            <th scope="col">{t('enrollment.email')}</th>
            <th scope="col">{t('enrollment.activeSince')}</th>
            <th scope="col">{t('enrollment.status')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((teacher) => (
            <tr key={teacher.personId}>
              <td>
                <Link to={`/teachers/${teacher.personId}`}>{teacher.displayName}</Link>
              </td>
              <td>{teacher.email}</td>
              <td>{teacher.activeSince ?? '—'}</td>
              <td>{teacher.active ? t('enrollment.active') : t('enrollment.inactive')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={styles.pager}>
        <Button disabled={!pager.canGoBack} onClick={pager.goBack}>
          {t('enrollment.previousPage')}
        </Button>
        <Button
          disabled={nextCursor === null}
          onClick={() => {
            if (nextCursor !== null) pager.goForward(nextCursor);
          }}
        >
          {t('enrollment.nextPage')}
        </Button>
      </div>

      {/*
        Invalidar a chave alcanca tambem o seletor de professor da matricula,
        que e outra consulta da mesma listagem. `refetch()` so refazia esta.
      */}
      <TeacherForm
        defaultTimezone={me.timezone}
        onCreated={() => {
          void queryClient.invalidateQueries({ queryKey: getListTeachersQueryKey() });
        }}
      />
    </div>
  );
}

function TeacherForm({
  defaultTimezone,
  onCreated,
}: {
  readonly defaultTimezone: string;
  readonly onCreated: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateTeacher();

  const [name, setName] = useState<CivilNameInput>(emptyCivilName());
  const [email, setEmail] = useState('');
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [locale, setLocale] = useState<Locale>('pt-BR');
  const [activeSince, setActiveSince] = useState('');

  // Mesma regra do cadastro de aluno: fuso que o `Intl` nao conhece nao sai
  // daqui, porque quem o recebe formata data com ele.
  const timezoneKnown = isValidTimezone(timezone);
  const canSave = isCivilNameComplete(name) && email.trim().length > 0 && timezoneKnown;

  const submit = (): void => {
    create.mutate(
      {
        data: {
          name: { civilName: name.civilName.trim(), maskSplitIndex: name.maskSplitIndex },
          email: email.trim(),
          timezone: timezone.trim(),
          locale,
          activeSince: activeSince === '' ? null : activeSince,
        },
      },
      {
        onSuccess: () => {
          setName(emptyCivilName());
          setEmail('');
          setActiveSince('');
          onCreated();
        },
      },
    );
  };

  return (
    <form
      className={styles.form}
      aria-label={t('enrollment.teachers.create')}
      onSubmit={(event) => {
        event.preventDefault();
        if (canSave) submit();
      }}
    >
      <h2>{t('enrollment.teachers.create')}</h2>
      {/*
        O valor por aula nao entra aqui de proposito: ele tem historico proprio
        e pertence a `putTeacherRate`, de outra onda.
      */}
      <p className={styles.maskHelp}>{t('enrollment.teachers.rateElsewhere')}</p>

      <CivilNameField value={name} onChange={setName} />

      <Field
        label={t('enrollment.email')}
        type="email"
        required
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      <Field
        label={t('enrollment.timezone')}
        hint={t('enrollment.timezoneHint')}
        required
        value={timezone}
        error={timezone.trim() !== '' && !timezoneKnown ? t('enrollment.timezoneInvalid') : undefined}
        onChange={(event) => setTimezone(event.target.value)}
      />
      <SelectField
        label={t('enrollment.locale')}
        required
        value={locale}
        options={LOCALES.map((option) => ({ value: option, label: t(`language.${option}`) }))}
        onChange={(value) => setLocale(value as Locale)}
      />
      <Field
        label={t('enrollment.activeSince')}
        type="date"
        value={activeSince}
        onChange={(event) => setActiveSince(event.target.value)}
      />

      {create.isError ? (
        <Alert tone="error">
          {isProblemError(create.error) && create.error.status === 409
            ? t('enrollment.teachers.conflict')
            : t('error.unexpected')}
        </Alert>
      ) : null}
      {create.isSuccess ? <Alert tone="success">{t('enrollment.teachers.created')}</Alert> : null}

      <div>
        <Button variant="primary" type="submit" loading={create.isPending} disabled={!canSave}>
          {t('enrollment.save')}
        </Button>
      </div>
    </form>
  );
}
